import { useCallback, useEffect, useRef, useState } from "react";
import {
  AgentReviewApiError,
  fetchAgentReviewForBill,
  fetchAgentReviewList,
  recheckBill,
  setFindingDisposition,
} from "../../data/api/agentReviewApi";
import {
  AgentReviewFilters,
  AgentReviewListResponse,
  AgentReviewRunDetail,
  AgentReviewStatus,
  FindingDisposition,
} from "../../domain/entities/AgentReviewEntities";

const EMPTY_COUNTS: Record<AgentReviewStatus, number> = {
  ready: 0,
  needs_attention: 0,
  checking: 0,
  not_checked: 0,
};

/**
 * ZOUR-67 — the list behind the Agent Review page.
 *
 * `available` is a first-class state, not an error: a tenant whose migrations
 * have not been run yet gets an empty list and a sentence explaining it, rather
 * than a red box implying something broke.
 */
export const useAgentReviewList = (initial: AgentReviewFilters = {}) => {
  const [filters, setFilters] = useState<AgentReviewFilters>({
    page: 1,
    per_page: 25,
    ...initial,
  });
  const [data, setData] = useState<AgentReviewListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [available, setAvailable] = useState(true);

  // A reload triggered by a re-check must not be raced by an in-flight one.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAgentReviewList(filters);
      if (id !== requestId.current) return;
      setAvailable(response.available !== false);
      setData(response);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err instanceof Error ? err.message : "Could not load the list");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const updateFilters = useCallback((next: Partial<AgentReviewFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...next,
      // Any filter change resets paging; staying on page 4 of a list that now
      // has two pages shows an empty table and looks broken.
      page: next.page ?? 1,
    }));
  }, []);

  return {
    items: data?.items ?? [],
    counts: data?.counts ?? EMPTY_COUNTS,
    pagination: data?.pagination ?? { page: 1, per_page: 25, total: 0, pages: 0 },
    filters,
    updateFilters,
    loading,
    error,
    available,
    reload: load,
  };
};

/**
 * One bill's latest run, for the drawer.
 *
 * Also owns Re-check. A re-check runs the whole review inside the request and
 * can outlast the browser's patience, so a request that dies without a verdict
 * (network error, 502/503/504) is not treated as a failed run — the hook polls
 * the bill until it lands. A request the server actually answered (401, 403,
 * 404, 400) is a definite no and is shown as one.
 */
export const useAgentReviewDetail = (billId: number | null) => {
  const [detail, setDetail] = useState<AgentReviewRunDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rechecking, setRechecking] = useState(false);

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const load = useCallback(async () => {
    if (billId === null) {
      setDetail(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setDetail(await fetchAgentReviewForBill(billId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the review");
    } finally {
      setLoading(false);
    }
  }, [billId]);

  useEffect(() => {
    load();
    return stopPolling;
  }, [load, stopPolling]);

  /** Poll until a run finishes, or until we have waited long enough to stop guessing. */
  const pollUntilSettled = useCallback(
    (startedFrom: number | null, attempt = 0) => {
      if (billId === null || attempt > 40) {
        setRechecking(false);
        return;
      }
      pollTimer.current = setTimeout(async () => {
        try {
          const next = await fetchAgentReviewForBill(billId);
          const run = next.run;
          const settled =
            run &&
            run.id !== startedFrom &&
            (run.status === "done" || run.status === "error");
          setDetail(next);
          if (settled) {
            setRechecking(false);
            return;
          }
        } catch {
          // A poll that fails is not a run that failed; keep waiting.
        }
        pollUntilSettled(startedFrom, attempt + 1);
      }, 5000);
    },
    [billId],
  );

  const recheck = useCallback(async () => {
    if (billId === null) return;
    const previousRunId = detail?.run?.id ?? null;
    setRechecking(true);
    setError(null);
    stopPolling();
    try {
      await recheckBill(billId);
      await load();
      setRechecking(false);
    } catch (err) {
      const indefinite = err instanceof AgentReviewApiError && err.isIndefinite;
      const message = err instanceof Error && err.message ? err.message : "";

      if (!indefinite) {
        // The server answered, and the answer was no — a 403 without
        // `bill.edit`, a 404, a bad request. Nothing is running, so saying
        // "still watching" would leave a spinner up for two minutes over a run
        // that will never arrive.
        setError(message || "The re-check could not be started.");
        setRechecking(false);
        return;
      }

      // No verdict from the server. The backend sets `ignore_user_abort(true)`,
      // so the run may well be going anyway — watch for it.
      setError(
        message
          ? `${message} — still watching for the result.`
          : "Still watching for the result.",
      );
      pollUntilSettled(previousRunId);
    }
  }, [billId, detail?.run?.id, load, pollUntilSettled, stopPolling]);

  const disposition = useCallback(
    async (findingId: number, value: FindingDisposition) => {
      const updated = await setFindingDisposition(findingId, value);
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              findings: prev.findings.map((f) =>
                f.id === findingId ? { ...f, ...updated } : f,
              ),
            }
          : prev,
      );
    },
    [],
  );

  return { detail, loading, error, rechecking, reload: load, recheck, disposition };
};
